// Очередь исходящих операций.
//
// Мутации ядра пишутся сюда, а не теряются при отсутствии сети. Тихая потеря действия —
// худший класс бага для офлайн-системы: кухня уверена, что списала, а в мониторинге
// запись висит активной.

import { isSupportSession, SupportReadOnlyError } from '@/shared/auth';
import { t } from '@/shared/i18n';

import { offlineDb, type QueuedOp, type QueuedOpType } from './db';

/** Не из будущего и не старше суток. Выход за границы — явное отклонение. */
export const ACTED_AT_FUTURE_TOLERANCE_MS = 60_000;
// Семь дней — окно офлайн-операции (T-037). Столько же принимает сервер;
// клиентская граница на сутки отвергала бы легитимное действие из очереди,
// пролежавшей на планшете без сети три дня, ещё до попытки отправки.
export const ACTED_AT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export class ActedAtOutOfRange extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ActedAtOutOfRange';
  }
}

export type EnqueueInput = {
  clientId: string;
  type: QueuedOpType;
  locationId: string;
  /**
   * Автор действия.
   *
   * Фиксируется В МОМЕНТ ТАПА, а не подставляется при флаше: за время офлайна
   * за устройство успевает сесть другой человек, и подпись «кто был активен
   * на момент синка» приписала бы ему чужие печати.
   */
  userId: string;
  payload: Record<string, unknown>;
  actedAt: string;
};

/**
 * Поставить операцию в очередь.
 *
 * Пары, схлопывающиеся локально, до сервера не доезжают вовсе — см. `collapsePair`.
 * Возвращает `null`, если операция схлопнулась с уже стоящей в очереди.
 */
export async function enqueue(
  input: EnqueueInput,
  now: number = Date.now(),
): Promise<QueuedOp | null> {
  // Саппорт-сессия только на чтение (T-039). Отказ ЗДЕСЬ, а не ответом сервера: иначе
  // отклонённая операция клиента осела бы в очереди на диске устройства платформы.
  if (isSupportSession()) {
    throw new SupportReadOnlyError(t('support.readOnly'));
  }
  assertActedAtSane(input.actedAt, now);

  const collapsed = await collapsePair(input);
  if (collapsed) {
    return null;
  }

  const op: QueuedOp = {
    ...input,
    createdAt: new Date(now).toISOString(),
    attempts: 0,
    status: 'pending',
  };

  await offlineDb.syncQueue.put(op);

  // Отправка ПРОБУЕТСЯ сразу же, а не ждёт события `online`.
  //
  // Раньше флаш вызывал только экран печати у себя, и всё остальное — аннулирование,
  // списание — оставалось в очереди навсегда: `online` не наступает, если сеть и так
  // есть. Баннер при этом исчезал, повар считал этикетку аннулированной, а на сервере
  // она оставалась активной. Кухня и система расходились молча — худший исход
  // для системы, которая существует ровно чтобы они сходились.
  notifyEnqueued();

  return op;
}

/**
 * Подписка на постановку в очередь.
 *
 * Очередь не знает, кто и как её отправляет, — она лишь сообщает, что появилась работа.
 * Прямой вызов флаша отсюда завязал бы хранилище на транспорт и сделал бы очередь
 * непроверяемой без сети.
 */
const listeners = new Set<() => void>();

export function onEnqueued(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyEnqueued(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Схлопнуть пару взаимоотменяющих операций, обе ещё стоящие в очереди.
 *
 * Это НЕ оптимизация трафика. Сервер отклонит `void` записи, которой ещё не видел,
 * и честное действие превратится в ошибку синка, которую повару нечем исправить.
 *
 * Возвращает `true`, если пара найдена и обе операции удалены.
 */
async function collapsePair(input: EnqueueInput): Promise<boolean> {
  const CANCELS: Partial<Record<QueuedOpType, QueuedOpType>> = {
    void: 'print',
    undispose: 'dispose',
  };

  const cancelled = CANCELS[input.type];
  if (!cancelled) {
    return false;
  }

  const existing = await offlineDb.syncQueue.get(input.clientId);
  // Схлопывать можно ТОЛЬКО то, что ещё не улетело: операция в статусе `syncing`
  // могла дойти до сервера, и её отмену обязан увидеть сервер тоже.
  if (!existing || existing.type !== cancelled || existing.status === 'syncing') {
    return false;
  }

  await offlineDb.syncQueue.delete(input.clientId);
  return true;
}

/** Проверка вменяемости момента тапа. */
export function assertActedAtSane(actedAt: string, now: number = Date.now()): void {
  const moment = new Date(actedAt).getTime();

  if (Number.isNaN(moment)) {
    throw new ActedAtOutOfRange('Некорректный момент действия.');
  }
  if (moment > now + ACTED_AT_FUTURE_TOLERANCE_MS) {
    // Часы устройства могут отставать на минуты — допуск это учитывает. Но действие
    // «из будущего» на часы вперёд означает сбитые часы, а на них считается срок
    // годности: молча принять его значит напечатать неверную дату.
    throw new ActedAtOutOfRange('Действие из будущего: проверьте часы устройства.');
  }
  if (moment < now - ACTED_AT_MAX_AGE_MS) {
    throw new ActedAtOutOfRange('Действие старше семи дней и не может быть принято.');
  }
}

/** Операции к отправке, строго в порядке постановки. */
/**
 * Вернуть прерванные операции в работу. Зовётся ОДИН раз при старте приложения.
 *
 * `syncing` — состояние на время запроса, но живёт оно в IndexedDB, то есть переживает
 * закрытие приложения. Снять его умеет только `sendOne`, доведя запрос до ответа или
 * до ошибки. Если приложение закрыли или перезагрузили в этот момент — а на кухне
 * моноблок гасят кнопкой питания, — операция остаётся `syncing` навсегда:
 *
 * - `queueSize()` её считает, и в шапке горит «ждёт отправки: 1»;
 * - `pendingOps()` её исключает, и ни один флаш её не подберёт;
 * - `failedOps()` её не видит, потому что статус не `error` — значит ни сообщения
 *   об ошибке, ни кнопки повтора.
 *
 * Отправлять её было НЕКОМУ, а счётчик утверждал обратное. Хуже молчания: экран
 * обещал доставку, которой не могло произойти (B-012).
 *
 * Возврат безопасен ровно потому, что приём идемпотентен по клиентскому `id`: если
 * запрос всё-таки дошёл до сервера перед обрывом, повторная доставка вернёт ту же
 * запись, а не создаст вторую этикетку.
 */
export async function resumeInterrupted(): Promise<number> {
  const stuck = await offlineDb.syncQueue.where('status').equals('syncing').toArray();
  for (const op of stuck) {
    await offlineDb.syncQueue.update(op.clientId, { status: 'pending' });
  }
  return stuck.length;
}

export async function pendingOps(): Promise<QueuedOp[]> {
  // FIFO по createdAt: операции над одной сущностью не переставляются. `void` раньше
  // своего `print` — это отказ сервера на пустом месте.
  const all = await offlineDb.syncQueue.orderBy('createdAt').toArray();
  // `error` ИСКЛЮЧЁН наравне с `syncing` (QA-001).
  //
  // `error` означает «сервер рассмотрел операцию и отказал»: повтор вернёт тот же
  // ответ. Пока такие операции попадали во флаш, они переотправлялись на КАЖДОМ
  // проходе — а с фоновой отправкой (B-023) это стало бесконечным потоком запросов:
  // 282 POST за 10 секунд простоя, 612 попыток у одной операции. Останавливалось
  // только перезагрузкой страницы.
  //
  // Существование `retryFailed()` — ровно про это: возврат в работу после отказа
  // делает ЧЕЛОВЕК кнопкой «Повторить». Пока автоматика переотправляла сама,
  // эта кнопка не имела смысла.
  //
  // Операции при этом никуда не деваются: их считает `failedOps()`, они видны
  // в индикаторе и ждут решения.
  return all.filter((op) => op.status !== 'syncing' && op.status !== 'error');
}

/**
 * Есть ли что отправлять ПРЯМО СЕЙЧАС — для фоновой проверки (QA-001).
 *
 * Отдельно от `queueSize()`: тот считает всё, включая `error` и `syncing`, и
 * фоновая отправка по нему запускалась там, где отправлять было нечего.
 */
/**
 * Записи точки, которые человек уже убрал с полки, а сервер об этом ещё не знает.
 *
 * Между тапом «Списать» и подтверждением сервера есть окно: операция лежит в очереди,
 * а `/print-jobs/active/` продолжает отдавать запись активной. Список, собранный в этом
 * окне, возвращает плитки на табло — на телефоне это увидено живьём после «Списать · 5»
 * (T-052): пять плиток исчезли и тут же вернулись, до перезагрузки страницы.
 *
 * `error` сюда НЕ попадает: сервер операцию рассмотрел и отказал, значит его состояние
 * и есть правда, а сама операция ждёт решения человека в индикаторе синхронизации
 * (`failedOps`). Прятать по ней запись значило бы соврать в другую сторону.
 */
export async function undeliveredTerminalIds(locationId: string): Promise<Set<string>> {
  const ops = await offlineDb.syncQueue.toArray();
  return new Set(
    ops
      .filter(
        (op) =>
          op.locationId === locationId &&
          (op.type === 'dispose' || op.type === 'void') &&
          op.status !== 'error',
      )
      .map((op) => op.clientId),
  );
}

/**
 * Все записи точки, по которым на устройстве лежит хоть какая-то операция.
 *
 * Нужны тому, кто чистит кэш по ответу сервера: напечатанная офлайн этикетка серверу
 * ещё не известна, и «её нет в активных» означает «он о ней не слышал», а не
 * «её списали». Удалить такую запись — потерять офлайн-журнал и FIFO.
 */
export async function queuedIds(locationId: string): Promise<Set<string>> {
  const ops = await offlineDb.syncQueue.toArray();
  return new Set(ops.filter((op) => op.locationId === locationId).map((op) => op.clientId));
}

export async function sendableCount(): Promise<number> {
  return (await pendingOps()).length;
}

export async function queueSize(): Promise<number> {
  return offlineDb.syncQueue.count();
}

export async function failedOps(): Promise<QueuedOp[]> {
  return offlineDb.syncQueue.where('status').equals('error').toArray();
}

/**
 * Сервер не признал субъекта на последнем флаше.
 *
 * Живёт в памяти, а не в базе, намеренно: это состояние «отправлять некому прямо
 * сейчас», а не свойство операции. Операции ни в чём не виноваты и остаются
 * `pending`; после перезапуска приложения первый же флаш выяснит состояние заново.
 */
let subjectRejectedByServer = false;

/** Ставит `syncEngine`: только он видит ответ сервера. */
export function setSubjectRejected(rejected: boolean): void {
  subjectRejectedByServer = rejected;
}

export function isSubjectRejected(): boolean {
  return subjectRejectedByServer;
}

export type RetryResult = {
  /** Сколько операций возвращено в очередь. */
  readonly retried: number;
  /** Повторять нечего до входа: сервер отказал субъекту, а не операциям. */
  readonly needsLogin: boolean;
};

/**
 * Сбросить ошибки в `pending` — ручной retry из интерфейса.
 *
 * Пока субъект отвергнут, повтор не делается ВООБЩЕ. Те же операции упрутся в тот же
 * отказ, а сброс статуса ещё и сотрёт текст ошибки: человек нажимает кнопку и видит,
 * как «ошибка» превращается в «в очереди», не сдвинувшись ни на операцию. Выход из
 * этого состояния один — вход, и вести туда обязан интерфейс, а не кнопка повтора.
 */
export async function retryFailed(): Promise<RetryResult> {
  if (subjectRejectedByServer) {
    return { retried: 0, needsLogin: true };
  }

  const failed = await failedOps();
  await offlineDb.syncQueue.bulkPut(
    failed.map((op) => ({ ...op, status: 'pending' as const, lastError: undefined })),
  );
  return { retried: failed.length, needsLogin: false };
}
